import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ISearchOptions } from 'api/catalog/models';

import { useQueryString } from './useQueryString';

export type RefinableFacets = Omit<
  ISearchOptions,
  'q' | 'page' | 'size' | 'sort'
>;

export type RefinableFacetsKey = keyof RefinableFacets;

export const REFINABLE_FACET_KEYS: RefinableFacetsKey[] = [
  'brandNames',
  'departmentNames',
  'categoryNames',
  'subCategoryNames',
  'dietaryLifestyleAttributes',
  'freeFromAttributes',
  'marketingAttributes',
  'organicCodes',
  'packConfigCodes',
  'sourceItemAttributes',
  'subCategoryIds',
  'hostSystems',
];

const getRefinableFacetsFromQueryString = (
  searchParams: URLSearchParams
): RefinableFacets => {
  const ret: RefinableFacets = {};
  REFINABLE_FACET_KEYS.forEach((key) => {
    const val = searchParams.get(key);
    ret[key] = val ? val.split(',') : [];
  });
  return ret;
};

const getGenericSelectedCount = (
  key: RefinableFacetsKey,
  facets: RefinableFacets
) => {
  const val = facets[key];
  return val ? val.length : 0;
};

export const useFacets = () => {
  const [searchParams] = useSearchParams();
  const { updateQueryString } = useQueryString();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [refinableFacets, setRefinableFacets] = useState<RefinableFacets>(
    getRefinableFacetsFromQueryString(searchParams)
  );

  const getCurrentlyAppliedCount = useCallback(
    (queryStringKey: RefinableFacetsKey) =>
      getGenericSelectedCount(
        queryStringKey,
        getRefinableFacetsFromQueryString(searchParams)
      ),
    [searchParams]
  );

  const isSearchApplied = useCallback(() => {
    const foundVal = searchParams.get('q');
    return foundVal !== null && foundVal.length > 0;
  }, [searchParams]);

  const getSelectedCount = useCallback(
    (queryStringKey: RefinableFacetsKey) =>
      getGenericSelectedCount(queryStringKey, refinableFacets),
    [refinableFacets]
  );

  const isFacetSelected = useCallback(
    (queryStringKey: RefinableFacetsKey, facetCode: string) => {
      const val = refinableFacets[queryStringKey];
      return val ? val.includes(facetCode) : false;
    },
    [refinableFacets]
  );

  const applyFacets = useCallback(() => {
    updateQueryString({ ...refinableFacets, q: searchTerm, page: 0 });
  }, [refinableFacets, searchTerm, updateQueryString]);

  const handleSelectionChange = useCallback(
    (queryStringKey: RefinableFacetsKey, facetCodes: string[] = []) => {
      setRefinableFacets((prev) => {
        const selectedValues = prev[queryStringKey];
        if (selectedValues) {
          const removed: string[] = [];
          const added: string[] = [];
          facetCodes.forEach((facet) => {
            if (selectedValues.includes(facet)) {
              removed.push(facet);
            } else {
              added.push(facet);
            }
          });
          return {
            ...prev,
            [queryStringKey]: [
              ...selectedValues.filter(
                (val) => !removed.map((codeFacet) => codeFacet).includes(val)
              ),
              ...added.map((codeFacet) => codeFacet),
            ],
          };
        } else {
          return {
            ...prev,
            [queryStringKey]: facetCodes,
          };
        }
      });
    },
    []
  );

  const handleSelectAllChange = useCallback(
    (queryStringKey: RefinableFacetsKey, allFacetCodes: string[] = []) => {
      const selectedCount = getSelectedCount(queryStringKey);
      if (selectedCount < allFacetCodes.length) {
        setRefinableFacets((prev) => ({
          ...prev,
          [queryStringKey]: allFacetCodes,
        }));
      } else {
        setRefinableFacets((prev) => ({
          ...prev,
          [queryStringKey]: undefined,
        }));
      }
    },
    [getSelectedCount]
  );

  const handleReset = useCallback(() => {
    setRefinableFacets(
      getRefinableFacetsFromQueryString(new URLSearchParams())
    );
    setSearchTerm('');
  }, []);

  const handleSearchChange = useCallback((val?: string) => {
    setSearchTerm(val || '');
  }, []);

  return {
    refinableFacets,
    searchTerm,
    getSelectedCount,
    getCurrentlyAppliedCount,
    isFacetSelected,
    isSearchApplied,
    applyFacets,
    handleSelectionChange,
    handleSelectAllChange,
    handleReset,
    handleSearchChange,
  };
};
